import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsData } from '../data/projectsData';
import Button from '../components/ui/Button';
import { ArrowLeft, ExternalLink, Cpu, CheckCircle2, Target, Lightbulb, Shield, Activity, Eye, Navigation, GraduationCap } from 'lucide-react';

const getDomainIcon = (domain) => {
  if (domain.includes('Health')) return <Activity className="w-5 h-5 text-emerald-600" />;
  if (domain.includes('Cyber')) return <Shield className="w-5 h-5 text-brand-600" />;
  if (domain.includes('Intelligence') || domain.includes('AI')) return <Eye className="w-5 h-5 text-indigoBrand-600" />;
  if (domain.includes('Mobility')) return <Navigation className="w-5 h-5 text-amber-600" />;
  if (domain.includes('Education') || domain.includes('Cognitive')) return <GraduationCap className="w-5 h-5 text-purple-600" />;
  return <Cpu className="w-5 h-5 text-brand-600" />;
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projectsData.find(
    (p) => p.id === id || (p.aliases && p.aliases.includes(id))
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 px-4">
        <div className="text-center max-w-md">
          <h2 className="text-3xl font-display font-bold text-slate-900 dark:text-white mb-3">Product Not Found</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">The technology platform you requested does not exist or may have been relocated.</p>
          <Button onClick={() => navigate('/')}>Return to Homepage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950 pt-28 pb-20">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-4xl mx-auto"
        >
          {/* Back Navigation */}
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors mb-8 group font-medium text-sm"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to All Products</span>
          </button>

          {/* Product Header Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-3xl p-8 md:p-10 shadow-card mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-brand-50 dark:bg-brand-950/50 text-brand-700 dark:text-brand-300 border border-brand-200/60 dark:border-brand-800">
                {getDomainIcon(project.domain)}
                {project.category || project.domain}
              </span>
              {project.badge && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                  {project.badge}
                </span>
              )}
            </div>

            <h1 className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-5 tracking-tight">
              {project.title}
            </h1>

            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
              {project.fullDescription}
            </p>

            {project.demoLink && (
              <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 flex items-center gap-4">
                <a href={project.demoLink} target="_blank" rel="noopener noreferrer">
                  <Button variant="primary" className="flex items-center gap-2">
                    Visit Live Platform <ExternalLink size={16} />
                  </Button>
                </a>
              </div>
            )}
          </div>

          {/* Detailed Specifications */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Key Capabilities */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-7 shadow-subtle">
              <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2.5">
                <CheckCircle2 className="text-brand-600 w-5 h-5" /> Key Architectural Features
              </h2>
              <ul className="space-y-3.5">
                {project.features.map((feature, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-3 text-slate-700 dark:text-slate-300 text-sm leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-600 dark:bg-brand-400 mt-2 shrink-0" />
                    <span>{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Technology Stack */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-7 shadow-subtle">
              <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2.5">
                <Cpu className="text-indigoBrand-600 w-5 h-5" /> Core Technology Stack
              </h2>
              <div className="flex flex-wrap gap-2.5 mb-6">
                {project.techStack.map((tech, i) => (
                  <span
                    key={i}
                    className="px-3.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 text-xs font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Engineered adhering to high-concurrency, low-latency, and zero-trust security architectural standards.
              </p>
            </div>

            {/* Target Use Cases */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-7 shadow-subtle">
              <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2.5">
                <Target className="text-emerald-600 w-5 h-5" /> Enterprise Use Cases
              </h2>
              <ul className="space-y-3">
                {project.useCases.map((useCase, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2 shrink-0" />
                    <span>{useCase}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Future Scope / Roadmap */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-7 shadow-subtle">
              <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2.5">
                <Lightbulb className="text-amber-600 w-5 h-5" /> Engineering Roadmap
              </h2>
              <ul className="space-y-3">
                {project.futureScope.map((scope, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-2 shrink-0" />
                    <span>{scope}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail;
